/**
 * Kalijy FoldCharge components.
 * Custom elements re-initialise automatically when the theme editor re-renders a section
 * (connectedCallback / disconnectedCallback), so no global state survives a section reload.
 * Cart writes go through Shopify's standard `Shopify.actions.updateCart`, which Horizon's
 * cart drawer and cart icon already listen to; nothing is stored in localStorage.
 */
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const T = () => window.KalijyStrings || {};
const fmt = (str, vars = {}) => String(str || '').replace(/\{\{\s*(\w+)\s*\}\}|\[(\w+)\]/g, (m, a, b) => (vars[a || b] ?? m));
const gid = (id) => (String(id).startsWith('gid://') ? String(id) : `gid://shopify/ProductVariant/${id}`);

/* ---------------------------------------------------------------- Carousel */
class KjCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-kj-track]');
    if (!this.track) return;
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.prev = this.querySelectorAll('[data-kj-prev]');
    this.next = this.querySelectorAll('[data-kj-next]');
    this.dots = this.querySelector('[data-kj-dots]');
    this.prev.forEach((b) => b.addEventListener('click', () => this.go(this.index - 1, true), { signal }));
    this.next.forEach((b) => b.addEventListener('click', () => this.go(this.index + 1, true), { signal }));
    this.track.addEventListener('scroll', () => this.queue(), { passive: true, signal });
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, true); }
    }, { signal });
    ['pointerenter', 'focusin', 'touchstart'].forEach((t) => this.addEventListener(t, () => (this.hold = true), { passive: true, signal }));
    ['pointerleave', 'focusout'].forEach((t) => this.addEventListener(t, () => { this.hold = false; this.pauseUntil = Date.now() + 2500; }, { signal }));
    this.addEventListener('touchend', () => { this.hold = false; this.pauseUntil = Date.now() + 6000; }, { passive: true, signal });
    this.ro = new ResizeObserver(() => this.update(true));
    this.ro.observe(this.track);
    document.addEventListener('shopify:block:select', (e) => {
      const slide = e.target.closest?.('[data-kj-slide]');
      if (slide && this.track.contains(slide)) this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft - this.pad(), behavior: 'auto' });
    }, { signal });
    const auto = Number(this.dataset.autoplay || 0);
    if (auto > 0) {
      this.io = new IntersectionObserver(([e]) => (this.visible = e.isIntersecting), { threshold: 0.3 });
      this.io.observe(this);
      this.timer = setInterval(() => this.tick(), auto);
    }
    this.update(true);
  }
  disconnectedCallback() {
    this.ac?.abort(); this.ro?.disconnect(); this.io?.disconnect(); clearInterval(this.timer); cancelAnimationFrame(this.raf);
  }
  pad() { return parseFloat(getComputedStyle(this.track).paddingInlineStart) || 0; }
  step() {
    const first = this.track.querySelector('[data-kj-slide]');
    if (!first) return this.track.clientWidth || 1;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return this.dataset.pageMode === 'page' ? this.track.clientWidth : first.getBoundingClientRect().width + gap;
  }
  pages() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    if (max <= 4) return 1;
    return Math.round(max / this.step()) + 1;
  }
  queue() { cancelAnimationFrame(this.raf); this.raf = requestAnimationFrame(() => this.update()); }
  update(rebuild = false) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    const pages = this.pages();
    this.index = x >= max - 4 ? pages - 1 : Math.round(x / this.step());
    const atStart = x <= 4, atEnd = x >= max - 4;
    this.prev.forEach((b) => (b.disabled = atStart && !this.dataset.loop));
    this.next.forEach((b) => (b.disabled = atEnd && !this.dataset.loop));
    this.toggleAttribute('data-single', pages <= 1);
    if (this.dots) {
      if (rebuild || this.dots.children.length !== pages) {
        this.dots.replaceChildren(...Array.from({ length: pages }, (_, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', fmt(T().go_to_slide, { index: i + 1, count: pages }));
          b.append(document.createElement('span'));
          b.addEventListener('click', () => this.go(i, true));
          return b;
        }));
      }
      [...this.dots.children].forEach((b, i) => b.setAttribute('aria-current', String(i === this.index)));
    }
  }
  go(i, user) {
    const pages = this.pages();
    if (this.dataset.loop) i = (i + pages) % pages;
    i = Math.max(0, Math.min(pages - 1, i));
    if (user) this.pauseUntil = Date.now() + 8000;
    const max = this.track.scrollWidth - this.track.clientWidth;
    this.track.scrollTo({ left: Math.min(max, i * this.step()), behavior: reduceMotion() ? 'auto' : 'smooth' });
  }
  tick() {
    if (reduceMotion() || this.hold || !this.visible || document.hidden || Date.now() < (this.pauseUntil || 0)) return;
    const pages = this.pages();
    if (pages <= 1) return;
    this.go(this.index >= pages - 1 ? 0 : this.index + 1, false);
  }
}

/* ---------------------------------------------------------------- Tabs (fade panels or scroll-to card) */
class KjTabs extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (!this.tabs.length) return;
    this.mode = this.dataset.mode || 'fade';
    this.track = this.querySelector('[data-kj-track]');
    this.caption = this.querySelector('[data-kj-caption]');
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i, true), { signal });
      tab.addEventListener('keydown', (e) => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const n = (i + d + this.tabs.length) % this.tabs.length;
        this.select(n, true);
        this.tabs[n].focus();
      }, { signal });
    });
    if (this.track) {
      this.track.addEventListener('scroll', () => {
        if (Date.now() - (this.autoScrollAt || 0) < 700) return;
        clearTimeout(this.st);
        this.st = setTimeout(() => this.syncFromScroll(), 120);
      }, { passive: true, signal });
    }
    ['pointerenter', 'focusin'].forEach((t) => this.addEventListener(t, () => (this.hold = true), { signal }));
    ['pointerleave', 'focusout'].forEach((t) => this.addEventListener(t, () => (this.hold = false), { signal }));
    this.addEventListener('touchstart', () => (this.pauseUntil = Date.now() + 8000), { passive: true, signal });
    document.addEventListener('shopify:block:select', (e) => {
      const i = this.panels().findIndex((p) => p.contains(e.target) || e.target.contains?.(p));
      if (i > -1) this.select(i, true);
    }, { signal });
    const auto = Number(this.dataset.autoplay || 0);
    if (auto > 0) {
      this.io = new IntersectionObserver(([e]) => (this.visible = e.isIntersecting), { threshold: 0.25 });
      this.io.observe(this);
      this.timer = setInterval(() => {
        if (reduceMotion() || this.hold || !this.visible || document.hidden || Date.now() < (this.pauseUntil || 0)) return;
        this.select((this.current + 1) % this.tabs.length, false);
      }, auto);
    }
    this.current = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
    if (this.mode === 'scroll') requestAnimationFrame(() => this.scrollTo(this.current, 'auto'));
  }
  disconnectedCallback() { this.ac?.abort(); this.io?.disconnect(); clearInterval(this.timer); clearTimeout(this.st); }
  panels() { return this.tabs.map((t) => document.getElementById(t.getAttribute('aria-controls'))).filter(Boolean); }
  select(i, user) {
    if (user) this.pauseUntil = Date.now() + 8000;
    this.current = i;
    const panels = this.panels();
    this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === i)); t.tabIndex = k === i ? 0 : -1; });
    if (this.mode === 'fade') {
      panels.forEach((p, k) => { p.toggleAttribute('data-active', k === i); p.setAttribute('aria-hidden', String(k !== i)); });
    } else {
      this.scrollTo(i, reduceMotion() ? 'auto' : 'smooth');
    }
    if (this.caption && panels[i]) this.caption.textContent = panels[i].dataset.caption || '';
  }
  scrollTo(i, behavior) {
    const card = this.panels()[i];
    if (!card || !this.track) return;
    this.autoScrollAt = Date.now();
    this.track.scrollTo({ left: card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2, behavior });
  }
  syncFromScroll() {
    const c = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0, bd = Infinity;
    this.panels().forEach((p, k) => { const d = Math.abs(p.offsetLeft + p.offsetWidth / 2 - c); if (d < bd) { bd = d; best = k; } });
    if (best !== this.current) {
      this.current = best;
      this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', String(k === best)); t.tabIndex = k === best ? 0 : -1; });
    }
  }
}

/* ---------------------------------------------------------------- Accordion */
class KjAccordion extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    this.querySelectorAll('[data-kj-acc-btn]').forEach((btn) => btn.addEventListener('click', () => this.toggle(btn), { signal: this.ac.signal }));
    document.addEventListener('shopify:block:select', (e) => {
      const btn = e.target.querySelector?.('[data-kj-acc-btn]');
      if (btn && this.contains(btn) && btn.getAttribute('aria-expanded') !== 'true') this.toggle(btn);
    }, { signal: this.ac.signal });
  }
  disconnectedCallback() { this.ac?.abort(); }
  toggle(btn) {
    const open = btn.getAttribute('aria-expanded') !== 'true';
    if (open && this.hasAttribute('data-single')) {
      this.querySelectorAll('[data-kj-acc-btn][aria-expanded="true"]').forEach((b) => b !== btn && this.set(b, false));
    }
    this.set(btn, open);
  }
  set(btn, open) {
    btn.setAttribute('aria-expanded', String(open));
    document.getElementById(btn.getAttribute('aria-controls'))?.toggleAttribute('data-open', open);
  }
}

/* ---------------------------------------------------------------- Video (autoplay in view, pause offscreen) */
class KjVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.addEventListener('play', () => this.toggleAttribute('playing', true), { signal });
    this.video.addEventListener('pause', () => this.toggleAttribute('playing', false), { signal });
    this.querySelectorAll('[data-kj-video-toggle]').forEach((b) => b.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      if (this.video.paused) { this.userPaused = false; this.play(); } else { this.userPaused = true; this.video.pause(); }
      this.label();
    }, { signal }));
    this.io = new IntersectionObserver(([e]) => {
      this.inView = e.isIntersecting && e.intersectionRatio >= 0.35;
      if (!this.inView) this.video.pause();
      else if (this.hasAttribute('data-autoplay') && !this.userPaused && !reduceMotion()) this.play();
    }, { threshold: [0, 0.35] });
    this.io.observe(this);
    this.label();
  }
  disconnectedCallback() { this.ac?.abort(); this.io?.disconnect(); }
  play() {
    const v = this.video;
    if (v.preload === 'none') v.preload = 'auto';
    v.play()?.catch(() => {});
    this.label();
  }
  label() {
    const b = this.querySelector('[data-kj-video-toggle]');
    if (b) b.setAttribute('aria-label', this.video.paused ? T().play_video || 'Play video' : T().pause_video || 'Pause video');
  }
}

/* ---------------------------------------------------------------- Dialogs (video modal, mobile menu, lightbox) */
document.addEventListener('click', (e) => {
  const opener = e.target.closest?.('[data-kj-open]');
  if (opener) {
    const dlg = document.getElementById(opener.dataset.kjOpen);
    if (dlg instanceof HTMLDialogElement) {
      e.preventDefault();
      dlg._kjOpener = opener;
      if (!dlg.open) dlg.showModal();
      opener.setAttribute('aria-expanded', 'true');
      dlg.dispatchEvent(new CustomEvent('kj:open', { detail: { opener } }));
      dlg.querySelectorAll('video[data-kj-autoplay]').forEach((v) => { if (!reduceMotion()) { v.currentTime = 0; v.play()?.catch(() => {}); } });
    }
    return;
  }
  const closer = e.target.closest?.('[data-kj-close]');
  if (closer) { closer.closest('dialog')?.close(); return; }
  if (e.target instanceof HTMLDialogElement && e.target.classList.contains('kj-dialog')) {
    const r = e.target.getBoundingClientRect();
    const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    if (!inside || e.target.dataset.backdropClose !== undefined) e.target.close();
  }
});
document.addEventListener('close', (e) => {
  const dlg = e.target;
  if (!(dlg instanceof HTMLDialogElement) || !dlg.classList.contains('kj-dialog')) return;
  dlg.querySelectorAll('video').forEach((v) => v.pause());
  const opener = dlg._kjOpener;
  if (opener) { opener.setAttribute('aria-expanded', 'false'); if (document.contains(opener)) opener.focus(); }
}, true);
/* In-page anchor links inside a dialog (mobile menu) close it first. */
document.addEventListener('click', (e) => {
  const a = e.target.closest?.('dialog.kj-dialog a[href]');
  if (!a) return;
  const url = new URL(a.href, location.href);
  if (url.pathname === location.pathname && url.hash) a.closest('dialog').close();
});

/* ---------------------------------------------------------------- Pause toggle (ticker / marquee) */
document.addEventListener('click', (e) => {
  const b = e.target.closest?.('[data-kj-pause-toggle]');
  if (!b) return;
  const host = b.closest('[data-kj-pausable]');
  if (!host) return;
  const paused = !host.classList.contains('kj-paused');
  host.classList.toggle('kj-paused', paused);
  b.setAttribute('aria-pressed', String(paused));
  b.setAttribute('aria-label', paused ? b.dataset.labelPlay : b.dataset.labelPause);
  b.querySelector('[data-kj-icon-pause]')?.toggleAttribute('hidden', paused);
  b.querySelector('[data-kj-icon-play]')?.toggleAttribute('hidden', !paused);
});

/* ---------------------------------------------------------------- Reveal on scroll */
class KjReveal extends HTMLElement {
  connectedCallback() {
    const els = [...this.querySelectorAll('[data-kj-reveal]')];
    if (!els.length || reduceMotion() || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-in')); return; }
    this.classList.add('kj-reveal-on');
    this.io = new IntersectionObserver((entries) => entries.forEach((en) => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); this.io.unobserve(en.target); }
    }), { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => this.io.observe(el));
    this.fail = setTimeout(() => els.forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in'); }), 1500);
  }
  disconnectedCallback() { this.io?.disconnect(); clearTimeout(this.fail); }
}

/* ---------------------------------------------------------------- Cart helpers */
async function fetchCart(signal) {
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', credentials: 'same-origin', signal });
  if (!res.ok) throw new Error(`cart ${res.status}`);
  return res.json();
}
async function waitForActions(ms = 4000) {
  const start = Date.now();
  while (!window.Shopify?.actions?.updateCart) {
    if (Date.now() - start > ms) return null;
    await new Promise((r) => setTimeout(r, 50));
  }
  return window.Shopify.actions;
}
async function updateCart(lines, source) {
  const actions = await waitForActions();
  if (!actions) throw Object.assign(new Error('actions-unavailable'), { code: 'NO_ACTIONS' });
  return actions.updateCart({ lines }, { event: { context: 'product', detail: { source } } });
}
function openCartDrawer() {
  if (window.Shopify?.actions?.openCart) return window.Shopify.actions.openCart();
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer?.open) drawer.open(); else location.href = (window.Shopify?.routes?.root || '/') + 'cart';
}
document.addEventListener('click', (e) => {
  const b = e.target.closest?.('[data-kj-cart-open]');
  if (!b) return;
  e.preventDefault();
  openCartDrawer();
});

/* ---------------------------------------------------------------- Gift kit sync (store-wide) */
const GiftSync = {
  cfg: null, busy: false, again: false, t: 0,
  init() {
    const el = document.getElementById('kj-gift-config');
    if (!el) return;
    try { this.cfg = JSON.parse(el.textContent); } catch { return; }
    if (!this.cfg?.gift_variant_id || !this.cfg?.qualifying_product_ids?.length) return;
    import('@shopify/events').then(({ StandardEvents }) => {
      document.addEventListener(StandardEvents.cartLinesUpdate, (ev) => {
        if (ev?.detail?.source === 'kalijy-gift-sync') return;
        const p = ev.promise || Promise.resolve();
        p.then((r) => { if (r?.detail?.source !== 'kalijy-gift-sync') this.schedule(); }).catch(() => this.schedule());
      });
    }).catch(() => {});
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.schedule(); });
    this.schedule();
  },
  schedule() { clearTimeout(this.t); this.t = setTimeout(() => this.run(), 250); },
  async run() {
    if (this.busy) { this.again = true; return; }
    this.busy = true;
    try {
      const cart = await fetchCart();
      const q = new Set(this.cfg.qualifying_product_ids.map(Number));
      const giftId = Number(this.cfg.gift_variant_id);
      const qualifying = cart.items.filter((i) => q.has(Number(i.product_id))).reduce((a, i) => a + i.quantity, 0);
      const kits = cart.items.filter((i) => Number(i.variant_id) === giftId || (this.cfg.gift_product_id && Number(i.product_id) === Number(this.cfg.gift_product_id)));
      const kitQty = kits.reduce((a, i) => a + i.quantity, 0);
      const lines = [];
      if (qualifying > 0 && kitQty === 0 && this.cfg.auto_add && this.cfg.available) {
        lines.push({ merchandiseId: gid(giftId), quantity: 1 });
      } else if (qualifying === 0 && kitQty > 0) {
        kits.forEach((k) => lines.push({ id: k.key, quantity: 0 }));
      } else if (kitQty > 1) {
        kits.forEach((k, i) => lines.push({ id: k.key, quantity: i === 0 ? 1 : 0 }));
      }
      if (lines.length) await updateCart(lines, 'kalijy-gift-sync');
    } catch (err) {
      if (err?.name !== 'AbortError') console.warn('[kalijy] gift sync skipped:', err?.message || err);
    } finally {
      this.busy = false;
      if (this.again) { this.again = false; this.schedule(); }
    }
  },
};

/* ---------------------------------------------------------------- Buy box (offers, colours, gift, add to cart, sticky bar) */
class KjBuyBox extends HTMLElement {
  connectedCallback() {
    const cfgEl = this.querySelector('script[data-kj-config]');
    if (!cfgEl) return;
    try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.warn('[kalijy] bad buy box config', e); return; }
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.form = this.querySelector('form[data-kj-form]');
    this.status = this.querySelector('[data-kj-status]');
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-kj-option]')) this.onOption();
      if (e.target.matches('[data-kj-offer]')) this.render();
      if (e.target.matches('[data-kj-gift-select]')) this.render();
    }, { signal });
    this.form?.addEventListener('submit', (e) => { e.preventDefault(); this.add(); }, { signal });
    this.querySelectorAll('[data-kj-add]').forEach((b) => b.addEventListener('click', (e) => {
      if (b.type === 'submit' && this.form?.contains(b)) return;
      e.preventDefault(); this.add();
    }, { signal }));
    this.querySelectorAll('[data-kj-thumb]').forEach((t) => t.addEventListener('click', () => this.showMedia(t.dataset.mediaId, true), { signal }));
    this.querySelectorAll('[data-kj-gal-prev],[data-kj-gal-next]').forEach((b) => b.addEventListener('click', () => this.stepMedia(b.hasAttribute('data-kj-gal-next') ? 1 : -1), { signal }));
    const main = this.querySelector('[data-kj-gallery-main]');
    if (main) {
      let x0 = null;
      main.addEventListener('pointerdown', (e) => (x0 = e.clientX), { signal });
      main.addEventListener('pointerup', (e) => { if (x0 !== null && Math.abs(e.clientX - x0) > 40) this.stepMedia(e.clientX < x0 ? 1 : -1); x0 = null; }, { signal });
    }
    this.setupSticky();
    this.onOption(true);
  }
  disconnectedCallback() { this.ac?.abort(); this.stickyIO?.disconnect(); document.documentElement.classList.remove('kj-sticky-on'); }

  get variant() {
    const sel = [...this.querySelectorAll('[data-kj-option]:checked')].map((i) => i.value);
    const vs = this.cfg.variants;
    return vs.find((v) => v.options.every((o, k) => sel[k] === undefined || o === sel[k])) || vs.find((v) => v.id === this.cfg.selected) || vs[0];
  }
  get offer() {
    const o = this.querySelector('[data-kj-offer]:checked');
    return this.cfg.offers?.[o ? Number(o.value) : 0] || { qty: 1, idx: 0 };
  }
  onOption(initial = false) {
    const v = this.variant;
    // Disable option values that cannot form an available variant together with the other selections.
    this.querySelectorAll('[data-kj-option]').forEach((input) => {
      const pos = Number(input.dataset.position);
      const sel = [...this.querySelectorAll('[data-kj-option]:checked')].map((i) => i.value);
      sel[pos] = input.value;
      const match = this.cfg.variants.find((x) => x.options.every((o, k) => o === sel[k]));
      input.closest('[data-kj-swatch]')?.toggleAttribute('data-unavailable', !match || !match.available);
    });
    if (!initial && this.dataset.updateUrl === 'true' && v) {
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url);
    }
    if (v?.featured_media_id && !initial) this.showMedia(String(v.featured_media_id), false);
    this.filterThumbs(v);
    this.render();
  }
  render() {
    const v = this.variant, o = this.offer, S = this.cfg.strings || {};
    if (!v) return;
    const p = (o.prices && o.prices[v.id]) || v.single;
    const set = (sel, val) => this.querySelectorAll(sel).forEach((el) => { if (val !== undefined) el.textContent = val; });
    set('[data-kj-color-label]', v.options[this.cfg.color_position ?? 0]);
    set('[data-kj-offer-name]', o.title || '');
    set('[data-kj-qty-label]', o.qty_label || '');
    set('[data-kj-price]', p.price);
    this.querySelectorAll('[data-kj-compare]').forEach((el) => { el.textContent = p.compare || ''; el.hidden = !p.compare; });
    this.querySelectorAll('[data-kj-save-pill]').forEach((el) => { el.textContent = p.pct ? fmt(S.save_pct, { pct: p.pct }) : ''; el.hidden = !p.pct; });
    this.querySelectorAll('[data-kj-savings]').forEach((el) => { el.textContent = p.save ? fmt(S.savings, { amount: p.save }) : (S.savings_none || ''); });
    this.querySelectorAll('[data-kj-offer-price]').forEach((el) => {
      const op = this.cfg.offers[Number(el.dataset.kjOfferPrice)]?.prices?.[v.id];
      if (op) el.textContent = op.price;
    });
    this.querySelectorAll('[data-kj-offer-compare]').forEach((el) => {
      const op = this.cfg.offers[Number(el.dataset.kjOfferCompare)]?.prices?.[v.id];
      if (op) { el.textContent = op.compare || ''; el.hidden = !op.compare; }
    });
    this.querySelectorAll('[data-kj-offer-img]').forEach((img) => {
      const map = JSON.parse(img.dataset.srcs || '{}');
      const src = map[v.options[this.cfg.color_position ?? 0]];
      if (src && img.getAttribute('src') !== src) { img.src = src; img.removeAttribute('srcset'); }
    });
    const idInput = this.form?.querySelector('input[name="id"]');
    if (idInput) idInput.value = v.id;
    const qtyInput = this.form?.querySelector('input[name="quantity"]');
    if (qtyInput) qtyInput.value = o.qty;
    const giftOk = this.giftReady();
    const can = v.available && giftOk.ok;
    this.querySelectorAll('[data-kj-add]').forEach((b) => {
      if (this.busy) return;
      b.disabled = !can;
      b.setAttribute('aria-disabled', String(!can));
      const tpl = b.dataset.ctaTemplate || S.add_to_cart;
      const label = !v.available ? S.sold_out : fmt(tpl, { offer: o.title || '', price: p.price, qty: o.qty });
      const l = b.querySelector('[data-kj-add-label]');
      if (l) l.textContent = b.hasAttribute('data-short') ? (v.available ? S.add_to_cart_short : S.sold_out) : label;
    });
    if (!giftOk.ok && giftOk.reason) this.say(giftOk.reason, 'info');
    else if (this.status?.dataset.kind === 'info') this.say('');
  }
  giftReady() {
    const g = this.cfg.gift;
    if (!g) return { ok: true };
    const sel = this.querySelector('[data-kj-gift-select]');
    if (sel && !sel.value) return { ok: false, reason: this.cfg.strings.gift_select };
    return { ok: true };
  }
  giftVariantId() {
    const g = this.cfg.gift;
    if (!g) return null;
    const sel = this.querySelector('[data-kj-gift-select]');
    const id = sel ? Number(sel.value) : g.variant_id;
    const gv = (g.variants || []).find((x) => x.id === id);
    if (gv && !gv.available) return null;
    if (!gv && !g.available) return null;
    return id;
  }
  say(msg, kind = '') {
    if (!this.status) return;
    this.status.textContent = msg || '';
    this.status.dataset.kind = kind;
    this.status.classList.toggle('kj-status--error', kind === 'error');
    this.status.classList.toggle('kj-status--ok', kind === 'ok');
  }
  setBusy(on) {
    this.busy = on;
    this.querySelectorAll('[data-kj-add]').forEach((b) => {
      b.setAttribute('aria-busy', String(on));
      b.disabled = on;
      const l = b.querySelector('[data-kj-add-label]');
      if (on && l) { b.dataset.prev = l.textContent; l.textContent = this.cfg.strings.adding; }
    });
    if (!on) this.render();
  }
  async add() {
    if (this.busy) return;
    const v = this.variant, o = this.offer, S = this.cfg.strings;
    if (!v?.available) { this.say(S.sold_out, 'error'); return; }
    const gr = this.giftReady();
    if (!gr.ok) { this.say(gr.reason, 'error'); this.querySelector('[data-kj-gift-select]')?.focus(); return; }
    this.setBusy(true);
    this.say('');
    try {
      const lines = [{ merchandiseId: gid(v.id), quantity: o.qty }];
      const giftId = this.giftVariantId();
      const g = this.cfg.gift;
      if (g && g.auto_add && giftId) {
        let hasKit = false;
        try {
          const cart = await fetchCart();
          hasKit = cart.items.some((i) => Number(i.variant_id) === Number(giftId) || Number(i.product_id) === Number(g.product_id));
        } catch { /* cart read failed: GiftSync reconciles after the add */ hasKit = true; }
        if (!hasKit) lines.push({ merchandiseId: gid(giftId), quantity: 1 });
      }
      let result;
      try {
        result = await updateCart(lines, 'kalijy-buy-box');
      } catch (err) {
        if (err?.code !== 'NO_ACTIONS') throw err;
        result = await this.ajaxAdd(lines);
        if (result.redirect) { location.href = result.redirect; return; }
      }
      const errs = result?.userErrors || [];
      if (errs.length) {
        this.say(errs.map((x) => x.message).join(' '), 'error');
        return;
      }
      (result?.warnings || []).length && this.say(result.warnings.map((w) => w.message).join(' '), 'info');
      if (!(result?.warnings || []).length) this.say(S.added, 'ok');
      openCartDrawer();
    } catch (err) {
      console.warn('[kalijy] add to cart failed', err);
      this.say(navigator.onLine === false ? S.offline : S.error, 'error');
    } finally {
      this.setBusy(false);
    }
  }
  async ajaxAdd(lines) {
    const root = window.Shopify?.routes?.root || '/';
    const items = lines.map((l) => ({ id: Number(String(l.merchandiseId).split('/').pop()), quantity: l.quantity }));
    const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { userErrors: [{ message: data.description || data.message || this.cfg.strings.error }] };
    return { redirect: `${root}cart` };
  }
  /* Gallery */
  thumbs() { return [...this.querySelectorAll('[data-kj-thumb]')].filter((t) => !t.hidden); }
  filterThumbs(v) {
    const all = [...this.querySelectorAll('[data-kj-thumb]')];
    if (!all.length) return;
    all.forEach((t) => { const vs = (t.dataset.variants || '').split(',').filter(Boolean); t.hidden = vs.length > 0 && !vs.includes(String(v.id)); });
    const cur = this.querySelector('[data-kj-gallery-item][data-active]');
    const visible = this.thumbs();
    if (!cur || !visible.some((t) => t.dataset.mediaId === cur.dataset.mediaId)) visible[0] && this.showMedia(visible[0].dataset.mediaId, false);
    else this.showMedia(cur.dataset.mediaId, false);
    const counter = this.querySelector('[data-kj-gal-counter]');
    if (counter) counter.hidden = visible.length < 2;
    this.querySelectorAll('[data-kj-gal-prev],[data-kj-gal-next]').forEach((b) => (b.hidden = visible.length < 2));
  }
  showMedia(id, focusThumb) {
    const items = this.querySelectorAll('[data-kj-gallery-item]');
    let found = false;
    items.forEach((it) => { const on = it.dataset.mediaId === String(id); it.toggleAttribute('data-active', on); it.setAttribute('aria-hidden', String(!on)); if (on) found = true; if (!on) it.querySelector('video')?.pause(); });
    if (!found) return;
    this.querySelectorAll('[data-kj-thumb]').forEach((t) => t.setAttribute('aria-current', String(t.dataset.mediaId === String(id))));
    const vis = this.thumbs();
    const i = vis.findIndex((t) => t.dataset.mediaId === String(id));
    const counter = this.querySelector('[data-kj-gal-counter]');
    if (counter && i > -1) counter.textContent = `${i + 1} / ${vis.length}`;
    const lb = this.querySelector('[data-kj-lightbox-img]');
    const item = this.querySelector(`[data-kj-gallery-item][data-media-id="${CSS.escape(String(id))}"] img`);
    if (lb && item) { lb.src = item.dataset.full || item.currentSrc || item.src; lb.alt = item.alt; }
    if (focusThumb) vis[i]?.focus();
  }
  stepMedia(d) {
    const vis = this.thumbs();
    if (!vis.length) return;
    const cur = vis.findIndex((t) => t.getAttribute('aria-current') === 'true');
    const n = (cur + d + vis.length) % vis.length;
    this.showMedia(vis[n].dataset.mediaId, false);
  }
  /* Sticky purchase bar: visible once the main CTA has scrolled above the viewport. */
  setupSticky() {
    const bar = this.querySelector('[data-kj-sticky]');
    const anchor = this.querySelector('[data-kj-cta-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    this.stickyIO = new IntersectionObserver(([e]) => {
      const show = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.toggleAttribute('data-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.inert = !show;
      document.documentElement.classList.toggle('kj-sticky-on', show);
    });
    this.stickyIO.observe(anchor);
    bar.inert = true;
  }
}

/* ---------------------------------------------------------------- Header: shadow when scrolled */
class KjHeader extends HTMLElement {
  connectedCallback() {
    this.onScroll = () => this.toggleAttribute('data-scrolled', window.scrollY > 4);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
}

const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
define('kj-carousel', KjCarousel);
define('kj-tabs', KjTabs);
define('kj-accordion', KjAccordion);
define('kj-video', KjVideo);
define('kj-reveal', KjReveal);
define('kj-buy-box', KjBuyBox);
define('kj-header', KjHeader);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => GiftSync.init(), { once: true });
else GiftSync.init();
